import { useEffect, useMemo, useRef, useState } from "react";

const SCAN_TIMEOUT_MS = 120;

const ScanTestPage = () => {
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const bufferRef = useRef('');
  const startedAtRef = useRef(0);

  const [currentValue, setCurrentValue] = useState('');
  const [lastScan, setLastScan] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();

    const handleWindowClick = () => {
      inputRef.current?.focus();
    };

    window.addEventListener('click', handleWindowClick);

    return () => {
      window.removeEventListener('click', handleWindowClick);
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const resetScan = () => {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    bufferRef.current = '';
    startedAtRef.current = 0;
    setCurrentValue('');
    setIsScanning(false);
  };

  const finalizeScan = () => {
    const value = bufferRef.current.trim();

    if (!value) {
      resetScan();
      return;
    }

    const endedAt = Date.now();
    const duration = startedAtRef.current ? endedAt - startedAtRef.current : 0;

    setLastScan(value);
    setScanHistory((prev) => [
      {
        id: endedAt,
        value,
        length: value.length,
        duration,
        endedAt: new Date(endedAt).toLocaleTimeString(),
      },
      ...prev,
    ]);

    window.alert(`Scan terminé : ${value}`);
    resetScan();
    inputRef.current?.focus();
  };

  const scheduleFinalize = () => {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      finalizeScan();
    }, SCAN_TIMEOUT_MS);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      finalizeScan();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    if (!startedAtRef.current) {
      startedAtRef.current = Date.now();
    }

    const nextValue = bufferRef.current + event.key;
    bufferRef.current = nextValue;
    setCurrentValue(nextValue);
    setIsScanning(true);
    scheduleFinalize();
  };

  const handleClearHistory = () => {
    setScanHistory([]);
    setLastScan('');
    resetScan();
    inputRef.current?.focus();
  };

  const status = useMemo(() => {
    if (isScanning) return 'Scan en cours';
    if (lastScan) return 'Prêt pour un nouveau scan';
    return 'En attente d’un scan';
  }, [isScanning, lastScan]);

  return (
    <div className="min-h-screen bg-slate-100 p-6 text-slate-900">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">Test de scan</h1>
              <p className="mt-2 text-sm text-slate-600">
                Clique n’importe où dans la page, puis scanne. Une alerte se déclenche à la fin du scan.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClearHistory}
              className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              Vider l’historique
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Statut</p>
              <p className="mt-2 text-lg font-semibold">{status}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Flux en cours</p>
              <p className="mt-2 break-all font-mono text-lg">{currentValue || '—'}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Dernier scan terminé</p>
              <p className="mt-2 break-all font-mono text-lg">{lastScan || '—'}</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Zone de capture
          </label>
          <input
            ref={inputRef}
            type="text"
            value={currentValue}
            onChange={() => {}}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 font-mono outline-none ring-0 focus:border-slate-500"
            placeholder="Scanne ici"
          />
          <p className="mt-3 text-sm text-slate-500">
            Le scan se termine soit sur Entrée, soit après une courte pause de saisie.
          </p>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Historique</h2>

          <div className="mt-4 space-y-3">
            {scanHistory.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                Aucun scan pour le moment.
              </div>
            ) : (
              scanHistory.map((scan) => (
                <div key={scan.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-slate-500">{scan.endedAt}</p>
                    <p className="text-sm text-slate-500">
                      {scan.length} caractères • {scan.duration} ms
                    </p>
                  </div>
                  <p className="mt-2 break-all font-mono text-base">{scan.value}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanTestPage;
